import styled, { css } from 'styled-components';

import { Link } from '@xing-com/link';
import {
  cornerRadiusXL,
  dropShadowS,
  motionEasingEnter,
  motionTimeM,
  scale005,
  scale010,
  scale020,
  scale080,
  spaceXXS,
  xdlColorControlBorderSoft,
  xdlColorControlFill,
  xdlColorFocusRing,
  xdlColorModalBorder,
  xdlColorSelected,
} from '@xing-com/tokens';

import type { CardVariant } from './card.types';

type CardTProps = {
  $autoposition?: boolean;
  $hasLink?: boolean;
  $maxHeight?: string;
  $maxWidth?: string;
  $minHeight?: string;
  $minWidth?: string;
  $noPadding?: boolean;
  $selected?: boolean;
  $variant: CardVariant;
};

type CardContentTProps = {
  $noPadding?: boolean;
};

// *** Components ***
export const Card = styled.div<CardTProps>`
  background-color: ${xdlColorControlFill};
  border-color: ${xdlColorControlBorderSoft};
  border-radius: ${cornerRadiusXL};
  border-style: solid;
  border-width: ${scale010};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${scale080};
  position: relative;
  transition-duration: ${motionTimeM};
  transition-property: transform, box-shadow;
  transition-timing-function: ${motionEasingEnter};

  ${(props) => css`
    ${props.$hasLink &&
    css`
      position: relative;

      &:hover,
      &:focus,
      &:focus-within {
        &::after {
          border-color: ${xdlColorSelected};
          border-radius: inherit;
          border-style: solid;
          border-width: calc(${scale010} + ${scale005});
          content: '';
          inset: -${spaceXXS};
          pointer-events: none;
          position: absolute;
        }
      }
    `};

    ${props.$autoposition &&
    css`
      & a,
      & button {
        position: relative;
        z-index: 9999;
      }
    `};

    ${props.$variant === 'elevated' &&
    css`
      border-color: ${xdlColorModalBorder};
      border-radius: ${cornerRadiusXL};
      border-width: ${scale010};
      box-shadow: ${dropShadowS};
    `};

    ${props.$selected &&
    css`
      border-color: ${xdlColorSelected};
    `};

    ${props.$noPadding &&
    css`
      padding: 0;
    `};

    ${props.$maxHeight &&
    css`
      max-height: ${props.$maxHeight};
    `};

    ${props.$maxWidth &&
    css`
      max-width: ${props.$maxWidth};
    `};

    ${props.$minHeight &&
    css`
      min-height: ${props.$minHeight};
    `};

    ${props.$minWidth &&
    css`
      min-width: ${props.$minWidth};
    `};
  `};
`;

export const CardContent = styled.div<CardContentTProps>`
  display: flex;
  flex-direction: column;

  ${(props) => css`
    ${props.$noPadding &&
    css`
      border-radius: calc(${cornerRadiusXL} - ${scale010});
      overflow: hidden;
    `};
  `};
`;

export const CardLink = styled(Link)`
  border-radius: calc(${cornerRadiusXL} - ${scale010});
  outline-offset: ${scale020};
  bottom: 0;
  height: 100%;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: calc(${spaceXXS} + ${scale010});
  }

  && {
    position: absolute;
    z-index: 0;
  }
`;
